import CartForm from '@archetype-themes/modules/cart-form'
import { EVENTS } from '@archetype-themes/utils/events'
import 'components/cart-item-remove'

class HeaderCart extends HTMLElement {
  constructor() {
    super()

    this.form = this.querySelector('form')
    
    // Cache for variant data to avoid repeated API calls
    this.variantCache = new Map()
    this.cacheExpiry = 5 * 60 * 1000 // 5 minutes
  }

  disconnectedCallback() {
    this.abortController.abort()
  }

  connectedCallback() {
    this.abortController = new AbortController()

    this.cartForm = new CartForm(this.form)
    this.cartForm.buildCart()

    document.addEventListener(EVENTS.ajaxProductAdded, this.handleCartChange.bind(this), {
      signal: this.abortController.signal
    })
  }

  async handleCartChange(evt) {
    this.cartForm.cartMarkup(evt.detail.product.sections['cart-ajax'])

    // Update item count in cart drawer header
    const itemCountElements = document.querySelectorAll('[data-item-count]')
    const cartAjaxElement = document.querySelector('[data-item-count][style*="display: none"]')
    
    if (cartAjaxElement) {
      const newItemCount = cartAjaxElement.textContent.trim()
      itemCountElements.forEach(element => {
        if (!element.hasAttribute('style')) {
          // Check if this element should have parentheses (like in cart drawer header)
          const parentElement = element.parentElement
          if (parentElement && (parentElement.classList.contains('cart__drawer-title') || 
                               parentElement.tagName.toLowerCase() === 'h2')) {
            element.textContent = `(${newItemCount})`
          } else {
            element.textContent = newItemCount
          }
        }
      })
    }

    // Update original total price
    this.updateOriginalTotal()

    if (!evt?.detail?.preventCartOpen) {
      this.dispatchEvent(new CustomEvent(EVENTS.cartOpen, { bubbles: true }))
    }

    // Resets cart property so that the form submit button can work
    if (this.cartForm.cartItemsUpdated) {
      this.cartForm.cartItemsUpdated = false
    }
  }

  async getVariantData(variantId) {
    const now = Date.now()
    const cached = this.variantCache.get(variantId)
    
    // Return cached data if it exists and hasn't expired
    if (cached && (now - cached.timestamp) < this.cacheExpiry) {
      return cached.data
    }
    
    try {
      const response = await fetch(`/variants/${variantId}.js`)
      const data = await response.json()
      
      // Cache the result
      this.variantCache.set(variantId, {
        data,
        timestamp: now
      })
      
      return data
    } catch (error) {
      console.error(`Error fetching variant ${variantId}:`, error)
      return null
    }
  }

  async updateOriginalTotal() {
    try {
      // Get cart data to calculate original total
      const response = await fetch('/cart.js')
      const cart = await response.json()
      
      // Fast calculation - use cached data whenever possible
      let originalTotal = 0
      
      if (cart.items.length === 0) {
        originalTotal = 0
      } else {
        // Get unique variant IDs that we need to fetch
        const variantIds = [...new Set(cart.items.map(item => item.variant_id))]
        const now = Date.now()
        
        // Separate cached vs uncached variants
        const uncachedIds = variantIds.filter(id => {
          const cached = this.variantCache.get(id)
          return !cached || (now - cached.timestamp) >= this.cacheExpiry
        })
        
        // Only fetch uncached variants
        if (uncachedIds.length > 0) {
          const variantPromises = uncachedIds.map(variantId => this.getVariantData(variantId))
          await Promise.all(variantPromises)
        }
        
        // Now calculate using cached data
        cart.items.forEach(item => {
          const cached = this.variantCache.get(item.variant_id)
          const variant = cached ? cached.data : null
          
          if (variant) {
            // Get prices in cents and convert to dollars
            const compareAtPriceCents = variant.compare_at_price || 0
            const regularPriceCents = variant.price || item.price || 0
            
            // Convert from cents to dollars (same format as Liquid template)
            const compareAtPrice = compareAtPriceCents / 100
            const regularPrice = regularPriceCents / 100
            
            if (compareAtPrice > regularPrice) {
              originalTotal += compareAtPrice * item.quantity
            } else {
              originalTotal += regularPrice * item.quantity
            }
          } else {
            // Fallback to cart item price if variant not found
            const regularPrice = item.price / 100
            originalTotal += regularPrice * item.quantity
          }
        })
      }

      // Update the original total display and hide/show based on comparison with subtotal
      const originalTotalElement = document.querySelector('[data-original-total]')
      const subtotalElement = document.querySelector('[data-subtotal]')
      
      if (originalTotalElement) {
        const formattedPrice = this.formatMoney(originalTotal)
        originalTotalElement.textContent = formattedPrice
        
        // Hide if original total equals subtotal (cart total)
        if (subtotalElement) {
          // Get current cart total for comparison
          const currentSubtotal = cart.total_price / 100 // Convert cents to dollars
          
          if (originalTotal === currentSubtotal) {
            originalTotalElement.style.display = 'none'
          } else {
            originalTotalElement.style.display = ''
          }
        }
      }
    } catch (error) {
      console.error('Error updating original total:', error)
    }
  }

  formatMoney(amount) {
    // Get money format from the form element
    const form = document.querySelector('[data-money-format]')
    let moneyFormat = '${{amount}}'
    
    if (form && form.dataset.moneyFormat) {
      moneyFormat = form.dataset.moneyFormat
    }
    
    // Format the amount (already in dollars, not cents)
    const formattedAmount = amount.toFixed(2)
    return moneyFormat.replace('{{amount}}', formattedAmount).replace('{{ amount }}', formattedAmount)
  }
}

customElements.define('header-cart-drawer', HeaderCart)
